import { Login } from '../models/forms/Login';
import { Reducer, Action } from 'redux';
import { IRootState } from '.';
import { fork, call, takeEvery, put, all, select } from 'redux-saga/effects';
import Api from '../api';
import { delay } from 'redux-saga/effects';
import ErrorResponse from '../models/ErrorResponse';
import LoginResponse from '../models/LoginResponse';
import { selectors as authSelectors } from '../reducers/Auth';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { actionCreators as authActionCreators } from '../reducers/Auth';
import ResetPassword from '../models/ResetPassword';

// Store
export interface IProfileState {
    confirm: {
        isConfirming: boolean;
        confirmFailed: boolean;
        passwordConfirmed: boolean;
    };
    setPassword: {
        isSetting: boolean;
        setPasswordFailed: boolean;
        setPasswordSuccess: boolean;
        passwordMatchFailed: boolean;
        isLoading: boolean;
    };
    passwordErrors: string[];
    resetPassword: {
        isLoading: boolean;
        resetFailed: boolean;
    };
    twoFactorSetup: {
        qrCodeImageUrl: string;
        manualEntryKey: string;
    };
}

const initialState: IProfileState = {
    confirm: {
        isConfirming: false,
        confirmFailed: false,
        passwordConfirmed: false,
    },
    setPassword: {
        isSetting: false,
        setPasswordFailed: false,
        setPasswordSuccess: false,
        passwordMatchFailed: false,
        isLoading: false,
    },
    passwordErrors: [],
    resetPassword: {
        isLoading: false,
        resetFailed: false,
    },
    twoFactorSetup: {
        qrCodeImageUrl: '',
        manualEntryKey: '',
    },
};

// Action Types
export enum actionTypes {
    CONFIRM = '[profile] CONFIRM',
    CONFIRM_SUCCESS = '[profile] CONFIRM_SUCCESS',
    CONFIRM_FAIL = '[profile] CONFIRM_FAIL',
    SET_PASSWORD = '[profile] SET_PASSWORD',
    SET_PASSWORD_SUCCESS = '[profile] SET_PASSWORD_SUCCESS',
    SET_PASSWORD_FAIL = '[profile] SET_PASSWORD_FAIL',
    PASSWORD_DO_NOT_MATCH = '[profile] PASSWORD_DO_NOT_MATCH',
    RESET_PASSWORD = '[profile] RESET_PASSWORD',
    RESET_PASSWORD_SUCCESS = '[profile] RESET_PASSWORD_SUCCES',
    RESET_PASSWORD_FAIL = '[profile] RESET_PASSWORD_FAILURE',
    ENABLE_TWO_FACTOR = '[profile] ENABLE_TWO_FACTOR',
    ENABLE_TWO_FACTOR_SUCCESS = '[profile] ENABLE_TWO_FACTOR_SUCCESS',
    ENABLE_TWO_FACTOR_FAILURE = '[profile] ENABLE_TWO_FACTOR_FAILURE',
    CONFIRM_TWO_FACTOR = '[profile] CONFIRM_TWO_FACTOR',
    CONFIRM_TWO_FACTOR_SUCCESS = '[profile] CONFIRM_TWO_FACTOR_SUCCESS',
    CONFIRM_TWO_FACTOR_FAILURE = '[profile] CONFIRM_TWO_FACTOR_FAILURE',
    DISABLE_TWO_FACTOR = '[profile] DISABLE_TWO_FACTOR',
    DISABLE_TWO_FACTOR_SUCCESS = '[profile] DISABLE_TWO_FACTOR_SUCCESS',
    DISABLE_TWO_FACTOR_FAILURE = '[profile] DISABLE_TWO_FACTOR_FAILURE',
    LOGIN_TWO_FACTOR = '[profile] LOGIN_TWO_FACTOR',
    LOGIN_TWO_FACTOR_SUCCESS = '[profile] LOGIN_TWO_FACTOR_SUCCESS',
    LOGIN_TWO_FACTOR_FAILURE = '[profile] LOGIN_TWO_FACTOR_FAILURE',
}

// Action Creator
export const actionCreators = {
    confirm: (confirmRequest: { login: Login; dialogName: string }) => {
        return { type: actionTypes.CONFIRM, payload: confirmRequest };
    },
    confirmSuccess: (confirmResponse: LoginResponse) => {
        return { type: actionTypes.CONFIRM_SUCCESS, payload: confirmResponse };
    },
    confirmFailure: (errorResponse: ErrorResponse) => {
        return { type: actionTypes.CONFIRM_FAIL, payload: errorResponse };
    },
    setNewPassword: (password: string) => {
        return { type: actionTypes.SET_PASSWORD, payload: password };
    },
    setNewPasswordSuccess: (confirmResponse: LoginResponse) => {
        return { type: actionTypes.SET_PASSWORD_SUCCESS, payload: confirmResponse };
    },
    setNewPasswordFailure: (errorResponse: ErrorResponse) => {
        return { type: actionTypes.SET_PASSWORD_FAIL, payload: errorResponse };
    },
    passwordsDoNotMatch: () => {
        return { type: actionTypes.PASSWORD_DO_NOT_MATCH };
    },
    forgotPasswordReset: (payload: ResetPassword) => {
        return { type: actionTypes.RESET_PASSWORD, payload: payload };
    },
    forgotPasswordResetSuccess: (payload: { response: string }) => {
        return { type: actionTypes.RESET_PASSWORD_SUCCESS, payload: payload };
    },
    forgotPasswordResetFailure: (errorResponse: ErrorResponse) => {
        return { type: actionTypes.RESET_PASSWORD_FAIL, payload: errorResponse };
    },
    enableTwoFactor: () => {
        return { type: actionTypes.ENABLE_TWO_FACTOR };
    },
    disableTwoFactor: () => {
        return { type: actionTypes.DISABLE_TWO_FACTOR };
    },
    enableTwoFactorSuccess: (response: { qrCodeImageUro: string; manualEntryKey: string }) => {
        return { type: actionTypes.ENABLE_TWO_FACTOR_SUCCESS, payload: response };
    },
    confirmTwoFactor: (payload: { twoFactorAuthPin: string; persistSession: boolean }) => {
        return { type: actionTypes.CONFIRM_TWO_FACTOR, payload };
    },
};

// Reducer
const reducer: Reducer<IProfileState> = (state: IProfileState = initialState, action: Action): IProfileState => {
    switch (action.type) {
        case actionTypes.CONFIRM: {
            const confirmState = {
                ...state.confirm,
                isConfirming: true,
            };
            return {
                ...state,
                confirm: confirmState,
            };
        }
        case actionTypes.CONFIRM_SUCCESS: {
            const confirmState = {
                ...state.confirm,
                confirmFailed: false,
                isConfirming: false,
                passwordConfirmed: true,
            };
            return {
                ...state,
                confirm: confirmState,
            };
        }
        case actionTypes.CONFIRM_FAIL: {
            const confirmState = {
                ...state.confirm,
                isConfirming: false,
                confirmFailed: true,
            };
            return {
                ...state,
                confirm: confirmState,
            };
        }
        case actionTypes.SET_PASSWORD: {
            const setPasswordState = {
                ...state.setPassword,
                isSetting: true,
            };
            return {
                ...state,
                setPassword: setPasswordState,
            };
        }
        case actionTypes.SET_PASSWORD_SUCCESS: {
            const setPasswordState = {
                ...state.setPassword,
                setPasswordFailed: false,
                isSettingPassword: false,
                setPasswordSuccess: true,
            };
            return {
                ...state,
                passwordErrors: [],
                setPassword: setPasswordState,
            };
        }
        case actionTypes.SET_PASSWORD_FAIL: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            const setPasswordState = {
                ...state.setPassword,
                setPasswordFailed: true,
                isSettingPassword: false,
                setPasswordSuccess: false,
            };
            return {
                ...state,
                passwordErrors: payload.message.split(' | '),
                setPassword: setPasswordState,
            };
        }
        case actionTypes.PASSWORD_DO_NOT_MATCH: {
            const setPasswordState = {
                ...state.setPassword,
                passwordMatchFailed: true,
            };
            return {
                ...state,
                setPassword: setPasswordState,
            };
        }
        case actionTypes.RESET_PASSWORD: {
            const resetPasswordState = {
                ...state.resetPassword,
                isLoading: true,
            };
            return {
                ...state,
                resetPassword: resetPasswordState,
            };
        }
        case actionTypes.RESET_PASSWORD_SUCCESS: {
            const resetPasswordState = {
                ...state.resetPassword,
                isLoading: false,
                resetFailed: false,
            };
            return {
                ...state,
                passwordErrors: [],
                resetPassword: resetPasswordState,
            };
        }
        case actionTypes.RESET_PASSWORD_FAIL: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            const resetPasswordState = {
                ...state.resetPassword,
                isLoading: false,
                resetFailed: true,
            };
            return {
                ...state,
                passwordErrors: payload.message.split(' | '),
                resetPassword: resetPasswordState,
            };
        }
        case actionTypes.ENABLE_TWO_FACTOR_SUCCESS: {
            const payload = (
                action as {
                    type: string;
                    payload: { qrCodeImageUrl: string; manualEntryKey: string };
                }
            ).payload;
            const qrState = payload;
            return {
                ...state,
                passwordErrors: [],
                twoFactorSetup: qrState,
            };
        }
        default:
            return state;
    }
};

export default reducer;

export const selectors = {
    confirmFailed: (state: IRootState): boolean => state.profile.confirm.confirmFailed,
    passwordMatchFailed: (state: IRootState): boolean => state.profile.setPassword.passwordMatchFailed,
    isLoading: (state: IRootState): boolean => state.profile.resetPassword.isLoading,
    qrImageCodeUrl: (state: IRootState): string => state.profile.twoFactorSetup.qrCodeImageUrl,
    manualEntryKey: (state: IRootState): string => state.profile.twoFactorSetup.manualEntryKey,
};

// Side-Effects
const sagas = {
    confirm: function* (action: { type: string; payload: { login: Login; dialogName: string } }): unknown {
        const [result] = yield all([call(Api.Auth.login, action.payload.login.username, action.payload.login.password), delay(200)]);

        if (result instanceof ErrorResponse) {
            yield put(actionCreators.confirmFailure(result));
        } else {
            yield put(actionCreators.confirmSuccess(result));

            if (action.payload.dialogName === 'enableTwoFactor') {
                yield put(actionCreators.enableTwoFactor());
            }

            if (action.payload.dialogName === 'disableTwoFactor') {
                yield put(actionCreators.disableTwoFactor());
                yield put(layoutActionCreators.hideDialog());
            }

            yield put(layoutActionCreators.showDialog(action.payload.dialogName));
        }
    },
    setNewPassword: function* (action: { type: string; payload: string }): unknown {
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Auth.setNewPassword, action.payload, jwt), delay(200)]);

        if (result instanceof ErrorResponse) {
            yield put(actionCreators.setNewPasswordFailure(result));
            console.log(result);
        } else {
            yield put(actionCreators.setNewPasswordSuccess(result));
            yield put(layoutActionCreators.hideDialog());
        }
    },
    enableTwoFactor: function* (action: { type: string; payload: string }): unknown {
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Auth.enableTwoFactor, jwt), delay(200)]);
        yield put(actionCreators.enableTwoFactorSuccess(result));
    },
    disableTwoFactor: function* (action: { type: string; payload: string }): unknown {
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Auth.disableTwoFactor, jwt), delay(200)]);
        yield put(authActionCreators.loginSuccess(result));
        yield put(layoutActionCreators.hideDialog());
    },
    confirmTwoFactor: function* (action: { type: string; payload: { twoFactorAuthPin: string; persistSesion: boolean } }): unknown {
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Auth.confirmTwoFactor, action.payload.twoFactorAuthPin, jwt), delay(200)]);
        result.persistSesion = action.payload.persistSesion;
        yield put(authActionCreators.loginSuccess(result));
        yield put(layoutActionCreators.hideDialog());
    },
    forgotPasswordReset: function* (action: { type: string; payload: ResetPassword }): unknown {
        const passwordReset = {
            password: action.payload.newPassword,
            resetKey: action.payload.resetKey,
        };
        const [result] = yield all([call(Api.Auth.forgotPasswordReset, passwordReset), delay(200)]);

        if (result instanceof ErrorResponse) {
            yield put(actionCreators.forgotPasswordResetFailure(result));
        } else {
            yield put(actionCreators.forgotPasswordResetSuccess(result));
            yield put(authActionCreators.logout());
        }
    },
};

const watches = {
    confirm: function* () {
        yield takeEvery(actionTypes.CONFIRM, sagas.confirm);
    },
    setNewPassword: function* () {
        yield takeEvery(actionTypes.SET_PASSWORD, sagas.setNewPassword);
    },
    forgotPasswordReset: function* () {
        yield takeEvery(actionTypes.RESET_PASSWORD, sagas.forgotPasswordReset);
    },
    enableTwoFactor: function* () {
        yield takeEvery(actionTypes.ENABLE_TWO_FACTOR, sagas.enableTwoFactor);
    },
    disableTwoFactor: function* () {
        yield takeEvery(actionTypes.DISABLE_TWO_FACTOR, sagas.disableTwoFactor);
    },
    confirmTwoFactor: function* () {
        yield takeEvery(actionTypes.CONFIRM_TWO_FACTOR, sagas.confirmTwoFactor);
    },
};

export function* saga() {
    yield fork(watches.confirm);
    yield fork(watches.setNewPassword);
    yield fork(watches.forgotPasswordReset);
    yield fork(watches.confirmTwoFactor);
    yield fork(watches.enableTwoFactor);
    yield fork(watches.disableTwoFactor);
}



// WEBPACK FOOTER //
// ./src/reducers/Profile.ts